import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import BusinessFormDialog, { EMPTY_BUSINESS } from "@/components/BusinessFormDialog";
import SellerQuestionnaire from "@/components/SellerQuestionnaire";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, CheckCircle2, Eye, Store, Sparkles } from "lucide-react";

const STATUS = {
  available: { label: "En ligne", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  pending: { label: "En attente de validation", cls: "bg-amber-100 text-amber-700", icon: Clock },
  sold: { label: "Cédée", cls: "bg-red-100 text-red-700", icon: CheckCircle2 },
};

export default function SellerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [qOpen, setQOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initial, setInitial] = useState(EMPTY_BUSINESS);

  const load = () => api.get("/seller/businesses").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setQOpen(true);
  const openEdit = (b) => { setEditing(b.business_id); setInitial({ ...EMPTY_BUSINESS, ...b }); setOpen(true); };

  const save = async (payload) => {
    try {
      if (editing) await api.put(`/seller/businesses/${editing}`, payload);
      else await api.post("/seller/businesses", payload);
      toast.success(editing ? "Annonce mise à jour" : "Annonce déposée — en attente de validation par PROAC Transmission");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await api.delete(`/seller/businesses/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFDFC]">
      <Header />
      <section className="bg-[#1B2A4A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs flex items-center gap-2"><Store className="w-4 h-4" /> Espace cédant</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl">Bonjour {user?.name?.split(" ")[0] || ""},</h1>
          <p className="mt-3 text-white/70 max-w-2xl">Déposez et gérez vos entreprises à céder. Répondez à un questionnaire guidé : notre IA rédige automatiquement votre présentation. Chaque annonce est validée par un conseiller PROAC Transmission avant publication.</p>
          <Button data-testid="seller-add-btn" onClick={openNew} className="mt-7 max-w-full whitespace-normal h-auto rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-5"><Sparkles className="w-4 h-4 mr-2" /> Compléter la fiche de mon entreprise</Button>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 flex-1 w-full">
        <h2 className="font-display text-2xl text-[#1B2A4A]">Mes annonces ({items.length})</h2>
        {items.length === 0 && <p className="mt-6 text-gray-400">Vous n'avez pas encore déposé d'annonce. Cliquez sur « Déposer une entreprise ».</p>}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((b) => {
            const st = STATUS[b.status] || STATUS.pending;
            const Ic = st.icon;
            return (
              <div key={b.business_id} data-testid={`seller-card-${b.business_id}`} className="bg-white border border-gray-200 flex flex-col">
                <div className="relative h-40 overflow-hidden">
                  <img src={b.hero_image} alt={b.title} className="w-full h-full object-cover" />
                  <span className={`absolute top-3 left-3 text-[11px] px-2 py-1 flex items-center gap-1 ${st.cls}`}><Ic className="w-3 h-3" /> {st.label}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg text-[#1B2A4A]">{b.title}</h3>
                  <p className="text-sm text-[#475569] mt-1">{[b.city, b.region].filter(Boolean).join(", ")} · {b.sector}</p>
                  {b.profitability_rate != null && <p className="text-sm font-semibold text-[#1B2A4A] mt-2">Rentabilité {b.profitability_rate}%</p>}
                  {b.admin_messages?.length > 0 && (() => { const m = b.admin_messages[b.admin_messages.length - 1]; return (
                    <div data-testid={`seller-message-${b.business_id}`} className={`mt-3 text-sm p-3 border-l-4 ${m.action === "request_changes" ? "border-amber-400 bg-amber-50" : "border-[#E0C173] bg-[#F8F6F0]"}`}>
                      <div className="text-xs text-gray-500">{m.action === "publish" ? "Fiche publiée" : m.action === "request_changes" ? "Modifications demandées" : "Message"} · PROAC Transmission · {new Date(m.created_at).toLocaleDateString("fr-FR")}</div>
                      {m.text && <p className="mt-1 text-[#1B2A4A] whitespace-pre-line">{m.text}</p>}
                    </div>); })()}
                  <div className="mt-4 flex gap-2 border-t border-gray-100 pt-4">
                    {b.status === "available" && <Button size="sm" variant="ghost" className="rounded-none" onClick={() => navigate(`/entreprise/${b.business_id}`)}><Eye className="w-4 h-4 mr-1" /> Voir</Button>}
                    <Button data-testid={`seller-edit-${b.business_id}`} size="sm" variant="ghost" className="rounded-none" onClick={() => openEdit(b)}><Pencil className="w-4 h-4 mr-1" /> Modifier</Button>
                    <Button data-testid={`seller-delete-${b.business_id}`} size="sm" variant="ghost" className="rounded-none text-red-600" onClick={() => remove(b.business_id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
      <BusinessFormDialog open={open} onOpenChange={setOpen} editing={editing} initial={initial} onSubmit={save} showStatus={false} />
      <SellerQuestionnaire open={qOpen} onOpenChange={setQOpen} onSubmitted={load} />
    </div>
  );
}
